import { FormControl, HeadlessFormItem } from "@/components/ui/form";
import { Input as ShadInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumericInput } from "@/components/ui/numeric-input";
import { cn } from "@/lib/utils";
import { useEffect, useId, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LabelProps } from "./AbstractForm";

const normalizeDecimal = (value: string): string => {
  if (!value) return value;
  return value.replace(",", ".");
};

function willBeEmptyAfterKey(
  value: string,
  selectionStart: number | null,
  selectionEnd: number | null,
  key: string,
): boolean {
  if (key !== "Backspace" && key !== "Delete") return false;
  const start = selectionStart ?? 0;
  const end = selectionEnd ?? start;
  if (start !== end) return true; // deleting selection
  if (key === "Backspace" && start <= 0) return false;
  if (key === "Delete" && start >= value.length) return false;
  const next =
    key === "Backspace"
      ? value.slice(0, start - 1) + value.slice(start)
      : value.slice(0, start) + value.slice(start + 1);
  return next.trim() === "";
}

function handleDeletionToZero(
  e: React.KeyboardEvent<HTMLInputElement>,
  getValue: () => string,
  setValue: (v: string) => void,
  onBlurSync: () => void,
) {
  const input = e.currentTarget;
  const value = getValue() ?? "";
  if (
    !willBeEmptyAfterKey(value, input.selectionStart, input.selectionEnd, e.key)
  )
    return;
  e.preventDefault();
  setValue("0");
  onBlurSync();
}

function handleCutSync(
  e: React.ClipboardEvent<HTMLInputElement>,
  onBlurSync: () => void,
) {
  const input = e.currentTarget;
  const value = input.value ?? "";
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? start;
  const isFullSelection =
    start === 0 && end === value.length && value.length > 0;
  if (!isFullSelection) return;
  // Allow default cut (preserves clipboard), then sync after value updates
  setTimeout(() => {
    onBlurSync();
  }, 0);
}

function getFractionalInch(inch: number, max: number = 8): string | number {
  // Initialize variables with proper types
  let result: string | number = "";
  let numerator: number = Math.round((inch - Math.floor(inch)) / (1 / max));
  let denominator: number = max;
  const wholeNumber: number = Math.floor(inch);

  // Simplify the fraction by dividing both numerator and denominator by 2
  // until one of them becomes odd
  while (numerator % 2 === 0 && denominator % 2 === 0) {
    numerator /= 2;
    denominator /= 2;
  }

  // Handle the case where the fraction equals 1
  if (numerator === denominator) {
    return wholeNumber + 1;
  }

  // Build the result string
  if (wholeNumber > 0) {
    result = wholeNumber.toString();
  }

  if (numerator > 0) {
    result = result
      ? `${result} ${numerator}/${denominator}`
      : `${numerator}/${denominator}`;
  }

  return result || 0;
}

/**
 * Converts a string of cm to a string of inches
 * "23.6543" -> "9 1/4"
 * "" -> ""
 * invalid value -> ""
 */
const cmToInchString = (cm: string, valueToDivide: number): string => {
  if (cm === "") return "";
  const normalizedCm = normalizeDecimal(cm);
  const value = Number(normalizedCm);
  if (isNaN(value)) return "";
  const totalInches = Math.round((value / valueToDivide) * 100) / 100;
  const finalInches = getFractionalInch(Math.round(totalInches * 100) / 100, 8);
  return finalInches.toString();
};

/**
 * Converts a string of inches to a string of cm
 * "9 1/4" -> "23.5"
 * "" -> ""
 * invalid value -> ""
 */
const inchStringToCm = (inchStr: string): string => {
  if (inchStr === "") return "";
  const normalizedStr = normalizeDecimal(inchStr);
  const [whole, fraction] = normalizedStr.split(" ") as [string, string?];
  const [numerator, denominator] = fraction
    ? fraction.split("/").map(Number)
    : [0, 1];
  const fractionalValue = numerator / (denominator || 1);

  const inches = Number(whole) + fractionalValue;
  if (isNaN(inches)) return "";

  return (Math.round(inches * 2.54 * 100) / 100).toString();
};

type DimensionField = Omit<LocaleDimensionsInputField, "name"> & {
  labelCm: string;
  nameCm: string;
  labelIn: string;
  nameIn: string;
};
type DimensionsInputProps = {
  dimensions: DimensionField[];
  /** If set, all fields will be defined as children of this name */
  parentName: string;
  needSurface?: boolean;
  needVolume?: boolean;

  // should automatically show the surface if the depth is undefined,
  // or the volume if the depth is defined
  volumeAutomaticToggle?: boolean;
} & LabelProps;

const DimensionsInput = ({
  dispLabel,
  label,
  dimensions,
  parentName,
  needSurface,
  needVolume,
  volumeAutomaticToggle,
}: DimensionsInputProps) => {
  const fieldId = useId();
  const form = useFormContext();
  const initializedDefaultsRef = useRef(false);

  const getFieldName = (name: string) =>
    parentName ? `${parentName}.${name}` : name;

  const cmVolumeRegistration = form.register(getFieldName("cm_volume"));
  form.register(getFieldName("in_surface"));
  form.register(getFieldName("in_volume"));

  // Initialize dimension inputs to "0" once on mount if empty/undefined
  useEffect(() => {
    if (initializedDefaultsRef.current) return;
    const namesToInit = [
      ...dimensions.map((d) => getFieldName(d.nameCm)),
      ...dimensions.map((d) => getFieldName(d.nameIn)),
    ];
    namesToInit.forEach((n) => {
      const current = form.getValues(n);
      if (current === undefined || current === null || current === "") {
        form.setValue(n, "0");
      }
    });
    initializedDefaultsRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCmBlur = (name: string, value: string) => {
    // Remove parent name from field name
    const nameSuffix = name.split(".").pop();
    const dimension = dimensions.find(({ nameCm }) => nameCm === nameSuffix);
    if (!dimension) return;
    form.setValue(getFieldName(dimension.nameIn), cmToInchString(value, 2.54));
    handleSurfaceBlur();
    handleVolumeBlur();
  };

  const handleInBlur = (name: string, value: string) => {
    // Remove parent name from field name
    const nameSuffix = name.split(".").pop();
    const dimension = dimensions.find(({ nameIn }) => nameIn === nameSuffix);
    if (!dimension) return;

    // Prevent rounding cm value if the current one already matches the in value
    if (
      cmToInchString(form.getValues(getFieldName(dimension.nameCm)), 2.54) ===
      value
    ) {
      return;
    }
    form.setValue(getFieldName(dimension.nameCm), inchStringToCm(value));
    handleSurfaceBlur();
    handleVolumeBlur();
  };

  const handleSurfaceBlur = () => {
    console.log("handleSurfaceBlur");
    const cm_diameter_normalized = form.getValues(getFieldName("cm_diameter"))
      ? normalizeDecimal(form.getValues(getFieldName("cm_diameter")))
      : "";
    const cm_diameter = Number(cm_diameter_normalized);
    if (cm_diameter > 0 && !isNaN(cm_diameter)) {
      const cm_surface = Math.PI * Math.pow(cm_diameter / 2, 2);
      form.setValue(getFieldName("cm_surface"), cm_surface.toFixed(2));
      form.setValue(getFieldName("in_surface"), cm_surface / 6.452);
      return;
    }

    const cm_width_normalized = normalizeDecimal(
      form.getValues(getFieldName("cm_width")),
    );
    const cm_height_normalized = normalizeDecimal(
      form.getValues(getFieldName("cm_height")),
    );
    const cm_width = Number(cm_width_normalized);
    const cm_height = Number(cm_height_normalized);

    if (
      cm_width > 0 &&
      cm_height > 0 &&
      !isNaN(cm_width) &&
      !isNaN(cm_height)
    ) {
      const cm_surface = cm_width * cm_height;
      form.setValue(getFieldName("cm_surface"), cm_surface.toFixed(2));
      form.setValue(getFieldName("in_surface"), cm_surface / 6.452);
    } else {
      form.setValue(getFieldName("cm_surface"), "");
      form.setValue(getFieldName("in_surface"), "");
    }
  };

  const handleVolumeBlur = () => {
    const cm_diameter_normalized = form.getValues(getFieldName("cm_diameter"))
      ? normalizeDecimal(form.getValues(getFieldName("cm_diameter")))
      : "";
    const cm_diameter = Number(cm_diameter_normalized);

    if (cm_diameter > 0 && !isNaN(cm_diameter)) {
      const depth_raw = normalizeDecimal(
        form.getValues(getFieldName("cm_depth")),
      );
      const cm_depth = Number(depth_raw);
      if (cm_depth > 0 && !isNaN(cm_depth)) {
        const cm_volume = Math.PI * Math.pow(cm_diameter / 2, 2) * cm_depth;
        form.setValue(getFieldName("cm_volume"), cm_volume.toFixed(2));
        form.setValue(getFieldName("in_volume"), cm_volume / 16.387);
      } else {
        form.setValue(getFieldName("cm_volume"), "");
        form.setValue(getFieldName("in_volume"), "");
      }
      return;
    }

    const cm_depth_normalized = normalizeDecimal(
      form.getValues(getFieldName("cm_depth")),
    );
    const cm_width_normalized = normalizeDecimal(
      form.getValues(getFieldName("cm_width")),
    );
    const cm_height_normalized = normalizeDecimal(
      form.getValues(getFieldName("cm_height")),
    );
    const cm_width = Number(cm_width_normalized);
    const cm_height = Number(cm_height_normalized);
    const cm_depth = Number(cm_depth_normalized);

    if (
      cm_width > 0 &&
      cm_height > 0 &&
      cm_depth > 0 &&
      !isNaN(cm_width) &&
      !isNaN(cm_height) &&
      !isNaN(cm_depth)
    ) {
      const cm_volume = cm_width * cm_height * cm_depth;
      form.setValue(getFieldName("cm_volume"), cm_volume.toFixed(2));
      form.setValue(getFieldName("in_volume"), cm_volume / 16.387);
    } else {
      form.setValue(getFieldName("cm_volume"), "");
      form.setValue(getFieldName("in_volume"), "");
    }
  };

  // TODO: Something strange is happening here.
  // I need to wath the values to format the input correctly. We don't want inch like this (1 1/2) in the database.
  // We want 1.5 for example. It will be easier to search on it ater.
  form.watch(getFieldName("cm_surface"));
  form.watch(getFieldName("cm_volume"));
  const cmDepth = form.watch(getFieldName("cm_depth"));

  const shouldShowSurface =
    (volumeAutomaticToggle === true &&
      (cmDepth == undefined || cmDepth.length == 0)) ||
    needSurface;

  const shouldShowVolume =
    (volumeAutomaticToggle === true &&
      cmDepth != undefined &&
      cmDepth.length > 0) ||
    needVolume;

  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="flex flex-col gap-1">
        <Label
          htmlFor={fieldId}
          className="text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal"
        >
          {dispLabel !== false && (label ?? t(parentName + "Cm"))}
        </Label>
        <p className="text-primary text-[12px] font-normal font-['Raleway'] leading-normal">
          {t(parentName + "CmSubtitle")}
        </p>
      </div>

      <div id={fieldId} className="flex flex-col gap-3">
        <LocaleDimensionsInput
          fields={dimensions.map(
            ({
              label: _l,
              labelCm,
              nameCm,
              nameIn: _ni,
              labelIn: _li,
              ...props
            }) => ({
              ...props,
              name: getFieldName(nameCm),
              placeholder: labelCm,
            }),
          )}
          unit="cm"
          onFieldBlur={handleCmBlur}
        />
        {shouldShowSurface && (
          <div className="flex items-center gap-3 w-1/3">
            <Label>{t("surface")}</Label>
            <NumericInput
              unit="cm²"
              readOnly
              name={getFieldName("cm_surface")}
              value={
                form.getValues(getFieldName("cm_surface"))?.toString() ?? ""
              }
              valueIsNumericString
              className="w-fit"
            />
          </div>
        )}
        {shouldShowVolume && (
          <div className="flex items-center gap-3 w-1/3">
            <Label>{t("volume")}</Label>
            <ShadInput
              unit="cm³"
              {...cmVolumeRegistration}
              readOnly
              className="w-fit"
            />
          </div>
        )}
        <Label
          htmlFor={fieldId}
          className="text-slate-700 text-sm font-semibold font-['Raleway'] leading-normal mt-5"
        >
          {dispLabel !== false && (label ?? t(parentName + "Inch"))}
        </Label>
        <LocaleDimensionsInput
          fields={dimensions.map(
            ({
              label: _l,
              nameIn,
              labelIn,
              nameCm: _nc,
              labelCm: _lc,
              ...props
            }) => ({
              ...props,
              name: getFieldName(nameIn),
              placeholder: labelIn,
            }),
          )}
          unit="inch"
          onFieldBlur={handleInBlur}
        />
        {needSurface && (
          <div className="flex items-center gap-3 w-1/3">
            <Label>{t("surface")}</Label>
            <ShadInput
              unit="sq in"
              className="w-fit"
              name={getFieldName("in_surface")}
              value={cmToInchString(
                form.getValues(getFieldName("in_surface"))?.toString() ?? "",
                1,
              )}
              readOnly
            />
          </div>
        )}
        {needVolume && (
          <div className="flex items-center gap-3 w-1/3">
            <Label>{t("volume")}</Label>
            <ShadInput
              unit="cu in"
              className="w-fit"
              name={getFieldName("in_volume")}
              value={cmToInchString(
                form.getValues(getFieldName("in_volume"))?.toString() ?? "",
                1,
              )}
              readOnly
            />
          </div>
        )}
      </div>
    </div>
  );
};

type LocaleDimensionsInputField = React.ComponentPropsWithoutRef<
  typeof ShadInput
> & { name: string };
type LocaleDimensionsInputProps = {
  fields: LocaleDimensionsInputField[];
  unit: "cm" | "inch";
  onFieldBlur?: (name: string, value: string) => void;
};

const LocaleDimensionsInput = ({
  fields,
  unit,
  onFieldBlur,
}: LocaleDimensionsInputProps) => {
  const form = useFormContext();

  return (
    <HeadlessFormItem>
      <FormControl>
        <div className="flex items-center border rounded-md space-x-2 overflow-hidden">
          {fields
            .flatMap(({ name, className, ...props }, i) => [
              unit === "cm" ? (
                <Controller
                  key={name}
                  name={name}
                  control={form.control}
                  render={({ field }) => (
                    <NumericInput
                      className={cn(
                        "border-none shadow-none text-center",
                        className,
                      )}
                      value={
                        !isNaN(parseFloat(field.value))
                          ? parseFloat(field.value)
                          : undefined
                      }
                      onValueChange={(v) => field.onChange(v?.toString())}
                      onKeyDown={(e) =>
                        handleDeletionToZero(
                          e,
                          () => String(form.getValues(name) ?? ""),
                          (v) => form.setValue(name, v),
                          () => {
                            const value = normalizeDecimal(
                              String(form.getValues(name) ?? ""),
                            );
                            onFieldBlur?.(name, value);
                          },
                        )
                      }
                      onCut={(e) =>
                        handleCutSync(e, () => {
                          const value = normalizeDecimal(
                            String(form.getValues(name) ?? ""),
                          );
                          onFieldBlur?.(name, value);
                        })
                      }
                      onBlur={() => {
                        const value = normalizeDecimal(field.value);
                        onFieldBlur?.(name, value);
                        props.onBlur?.(field.value);
                      }}
                      valueIsNumericString
                    />
                  )}
                />
              ) : (
                <ShadInput
                  key={name}
                  {...props}
                  className={cn(
                    "border-none shadow-none text-center",
                    className,
                  )}
                  {...form.register(name)}
                  onKeyDown={(e) =>
                    handleDeletionToZero(
                      e,
                      () => String(form.getValues(name) ?? ""),
                      (v) => form.setValue(name, v),
                      () => {
                        const value = normalizeDecimal(
                          String(form.getValues(name) ?? ""),
                        );
                        onFieldBlur?.(name, value);
                      },
                    )
                  }
                  onCut={(e) =>
                    handleCutSync(e, () => {
                      const value = normalizeDecimal(
                        String(form.getValues(name) ?? ""),
                      );
                      onFieldBlur?.(name, value);
                    })
                  }
                  onBlur={(e) => {
                    const value = normalizeDecimal(e.target.value);
                    onFieldBlur?.(name, value);
                    props.onBlur?.(e);
                  }}
                />
              ),
              <span key={`${name}_sep_${i}`} className="py-2 px-3 text-primary">
                x
              </span>,
            ])
            .slice(0, -1)}

          <div className="bg-white h-full border-l p-2">
            <small>{unit}</small>
          </div>
        </div>
      </FormControl>
    </HeadlessFormItem>
  );
};

export const TwoDimensionalInput = (
  props: Omit<DimensionsInputProps, "dimensions">,
) => {
  const { t } = useTranslation();
  return (
    <DimensionsInput
      {...props}
      dimensions={[
        {
          labelCm: t("heightCm"),
          nameCm: "cm_height",
          labelIn: t("heightInch"),
          nameIn: "in_height",
        },
        {
          labelCm: t("widthCm"),
          nameCm: "cm_width",
          labelIn: t("widthInch"),
          nameIn: "in_width",
        },
      ]}
    />
  );
};

export const ThreeDimensionalInput = (
  props: Omit<DimensionsInputProps, "dimensions">,
) => {
  const { t } = useTranslation();
  return (
    <DimensionsInput
      {...props}
      dimensions={[
        {
          labelCm: t("heightCm"),
          nameCm: "cm_height",
          labelIn: t("heightInch"),
          nameIn: "in_height",
        },
        {
          labelCm: t("widthCm"),
          nameCm: "cm_width",
          labelIn: t("widthInch"),
          nameIn: "in_width",
        },
        {
          labelCm: t("depthCm"),
          nameCm: "cm_depth",
          labelIn: t("depthInch"),
          nameIn: "in_depth",
        },
      ]}
    />
  );
};

export const FourDimensionalInput = (
  props: Omit<DimensionsInputProps, "dimensions">,
) => {
  const { t } = useTranslation();
  return (
    <DimensionsInput
      {...props}
      dimensions={[
        {
          labelCm: t("heightCm"),
          nameCm: "cm_height",
          labelIn: t("heightInch"),
          nameIn: "in_height",
        },
        {
          labelCm: t("widthCm"),
          nameCm: "cm_width",
          labelIn: t("widthInch"),
          nameIn: "in_width",
        },
        {
          labelCm: t("depthCm"),
          nameCm: "cm_depth",
          labelIn: t("depthInch"),
          nameIn: "in_depth",
        },
        {
          labelCm: t("diameterCm"),
          nameCm: "cm_diameter",
          labelIn: t("diameterInch"),
          nameIn: "in_diameter",
        },
      ]}
    />
  );
};

export default DimensionsInput;
